import { toRefs } from '@vueuse/core'
import type { Nullable, RvExpandedDetails } from '~/types'

/**
 * Wraps the getRvDetails util function with a reactive data source and
 * updates the global state if new data is provided.
 *
 * This way the RV data from the global state can be used, or a new RV object
 * can be provided to update the global state.
 *
 * Using toRefs also keeps reactivity after destructuring.
 *
 * @param data The new RV data to set
 * @returns The RV details
 */
export default function useRvDetails(data?: Ref<Nullable<RvExpandedDetails> | undefined>) {
  // The RV data from the global state.
  const rv = useSelectedRvDetails()

  const bookingEstimateParams = useBookingEstimateParams()
  const bookingEstimatePending = useBookingEstimatePending()
  const bookingEstimateError = useBookingEstimateError()
  const bookingEstimateData = useBookingEstimateData()

  // If a new RV object is provided, update the global state.
  if (data !== undefined) {
    watch(data, (newData) => {
      if (!newData) {
        return
      }

      const previousRvId = rv.value?.Id
      rv.value = newData

      // The booking estimate belongs to the selected RV, and both live in global SPA state.
      // A protection plan or add-on list picked for the previous RV would otherwise be sent
      // as an explicit guest choice for this one and suppress the default the server resolves
      // for the new listing, so the estimate is cleared here, where the RV actually changes.
      //
      // Compared by id rather than by identity: the same RV re-emitted, by a refetch for
      // example, must not wipe params a page has already seeded, and neither must the first
      // assignment into empty state.
      if (!previousRvId || previousRvId === newData.Id) {
        return
      }

      bookingEstimatePending.value = false
      bookingEstimateError.value = null
      bookingEstimateData.value = null
      bookingEstimateParams.value.addons = []
      bookingEstimateParams.value.protectionLevel = null
      // clear destination/delivery?
    }, {
      immediate: true,
    })
  }

  // Compute all the RV details.
  const rvDetails = computed(() => getRvDetails(rv?.value))

  // Return the object with `toRefs` to support reactive destructuring.
  return toRefs(rvDetails)
}
